import { useEffect } from 'react';
import { useAuth } from './useAuth.js';
import { useQuery } from '@tanstack/react-query';
import { userService, auditContext } from '@syscolect/domain';
import { useDispatchHookEvent } from '@rammetec/core';
import { useImpersonation } from './useImpersonation.js';
/**
 * Custom hook to get the current authenticated user and their profile data.
 * It encapsulates the logic for both Auth state and Firestore user data fetching,
 * providing a single, clean interface for components.
 *
 * Supports impersonation: if a user is being impersonated, it will fetch
 * and return the impersonated user's data instead of the authenticated user's data.
 */
export const useCurrentUser = () => {
    const [user, isAuthLoading, authError] = useAuth();
    // Use the shared impersonation hook which manages state via React Query
    const { impersonatedUserId } = useImpersonation();
    // Determine which user ID to use: impersonated user if impersonating, otherwise authenticated user
    const effectiveUserId = impersonatedUserId || user?.userId;
    const { data: currentUserData, isLoading: isUserDataLoading, error: userError } = useQuery({
        queryKey: ['user', effectiveUserId],
        queryFn: () => {
            // useQuery's `enabled` flag ensures `effectiveUserId` is defined when this runs
            return userService.getUserData(effectiveUserId);
        },
        enabled: !!effectiveUserId, // Only run the query if we have a user ID (either authenticated or impersonated)
    });
    // Feed the domain audit context so domain mutations stamp inclusion/last-modified
    // user ids (option B — set once per session; cleared on logout). Reflects the
    // effective user (impersonated id when impersonating).
    useEffect(() => {
        auditContext.setUserId(effectiveUserId ?? undefined);
    }, [effectiveUserId]);
    useDispatchHookEvent('useCurrentUser', {
        currentUserId: effectiveUserId,
        currentUserData,
        isLoading: isAuthLoading || isUserDataLoading,
        error: authError || userError,
        isImpersonating: !!impersonatedUserId,
        originalUserId: user?.userId,
    });
    return {
        currentUserId: effectiveUserId,
        currentUserData, // The user profile data from Firestore (includes id, email, name, etc.)
        isLoading: isAuthLoading || isUserDataLoading, // Combined loading state
        error: authError || userError, // Combined error state
        isImpersonating: !!impersonatedUserId, // Whether impersonation is active
        originalUserId: user?.userId, // The original authenticated user ID
    };
};
//# sourceMappingURL=useCurrentUser.js.map